import { FlowParamsContext } from '@/app/FlowInitializer';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { producedDataKeysState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { DataKeyInfo } from '@/util/dataKeyUtils';
import { AlertTriangle } from 'lucide-react';
import { type ComponentPropsWithoutRef, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';

type AppDataKeyFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
> = Pick<ControllerProps<TFieldValues, TName, TTransformedValues>, 'control' | 'name'> &
    ComponentPropsWithoutRef<'div'> & {
        label?: string;
        smallLabel?: true;
        description?: string;
        placeholder?: string;
        mode: 'produce' | 'consume';
        currentNodeId: string;
    };

export function AppDataKeyField<T extends FieldValues = FieldValues, U extends FieldPath<T> = FieldPath<T>>({
    name,
    control,
    label,
    smallLabel,
    description,
    placeholder,
    mode,
    currentNodeId,
    ...rest
}: AppDataKeyFieldProps<T, U>) {
    const params = useContext(FlowParamsContext);
    const producedKeys = useAsyncAtomValue(producedDataKeysState(params)) ?? [];
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                const currentValue = (field.value as string) || '';
                const filteredSuggestions = currentValue
                    ? producedKeys.filter((k) => k.key.toLowerCase().includes(currentValue.toLowerCase()))
                    : producedKeys;

                const duplicateProducers =
                    mode === 'produce'
                        ? producedKeys.filter((k) => k.key === currentValue && k.nodeId !== currentNodeId)
                        : [];

                const isDisconnected =
                    mode === 'consume' &&
                    !!currentValue &&
                    !currentValue.includes('{{') &&
                    !producedKeys.some((k) => k.key === currentValue);

                return (
                    <FormItem {...rest}>
                        {label && <FormLabel className={smallLabel ? 'text-xs text-gray-400' : ''}>{label}</FormLabel>}
                        <FormControl>
                            <div ref={wrapperRef} className="relative">
                                <Input
                                    className="bg-white"
                                    placeholder={placeholder}
                                    {...field}
                                    value={currentValue}
                                    onFocus={() => setOpen(true)}
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setOpen(true);
                                    }}
                                    autoComplete="off"
                                />
                                {isDisconnected && (
                                    <AlertTriangle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500" />
                                )}
                                {open && filteredSuggestions.length > 0 && (
                                    <div className="absolute z-50 top-full left-0 w-full mt-1 rounded-md border bg-popover text-popover-foreground shadow-md">
                                        <DataKeySuggestionList
                                            suggestions={filteredSuggestions}
                                            currentNodeId={currentNodeId}
                                            onSelect={(key) => {
                                                field.onChange(key);
                                                setOpen(false);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </FormControl>
                        {description && <FormDescription>{description}</FormDescription>}
                        {duplicateProducers.length > 0 && (
                            <div className="text-xs text-amber-600">
                                Also produced by {duplicateProducers.map((p) => p.nodeId).join(', ')}
                            </div>
                        )}
                        {isDisconnected && (
                            <div className="text-xs text-amber-600">No producer node found for this data key</div>
                        )}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}

const DataKeySuggestionList = ({
    suggestions,
    currentNodeId,
    onSelect,
}: {
    suggestions: DataKeyInfo[];
    currentNodeId: string;
    onSelect: (key: string) => void;
}) => {
    const grouped = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const s of suggestions) {
            const ids = map.get(s.key);
            if (ids) {
                if (!ids.includes(s.nodeId)) ids.push(s.nodeId);
            } else {
                map.set(s.key, [s.nodeId]);
            }
        }
        return [...map.entries()];
    }, [suggestions]);

    return (
        <div className="max-h-[200px] overflow-y-auto flex flex-col py-1">
            {grouped.map(([key, nodeIds]) => (
                <button
                    key={key}
                    type="button"
                    className={`flex items-center justify-between px-3 py-1.5 text-sm hover:bg-accent cursor-pointer text-left ${
                        nodeIds.length === 1 && nodeIds[0] === currentNodeId ? 'text-muted-foreground' : ''
                    }`}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onSelect(key);
                    }}
                >
                    <span className="font-mono">{key}</span>
                    <span className="text-xs text-muted-foreground ml-2">{nodeIds.join(', ')}</span>
                </button>
            ))}
        </div>
    );
};
